import { useCallback, useSyncExternalStore } from 'react';
import type { ProjektVerortung } from '../../types';
import { verortungEingerichtet } from './projektVerortung';
import type { Speicher } from './sitzung';

/**
 * Geräte-Einstellung der Sensor-Verortung (docs/verortung-kamera-vertrag.md,
 * Paket A): Schalter in „App-Info“, nur auf diesem Gerät (localStorage).
 * - `aktiv`: aus ⇒ nirgends Sensoren/Einblendung, der Wizard nimmt die
 *   Systemkamera wie bisher.
 * - `einblendung`: Knopf „Einblendung aus/an“ in der Kamera.
 * - `fotostempel`: Balken mit Ort/Zeit im Foto — standardmäßig AUS.
 */

export interface VerortungGeraet {
    aktiv: boolean;
    einblendung: boolean;
    fotostempel: boolean;
}

export const STANDARD_GERAET: VerortungGeraet = Object.freeze({ aktiv: true, einblendung: true, fotostempel: false });

const SCHLUESSEL = 'sigeko.verortungGeraet.v1';
/** Eigenes Ereignis: gleiche Seite (der `storage`-Event kommt nur in ANDEREN Tabs an). */
const EREIGNIS = 'sigeko:verortungGeraet';

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

/** Jedes Feld einzeln: Boolean übernehmen, sonst der Wert aus `basis`. */
function geraetNormalisieren(roh: unknown, basis: VerortungGeraet): VerortungGeraet {
    const o = roh && typeof roh === 'object' && !Array.isArray(roh) ? (roh as Partial<Record<keyof VerortungGeraet, unknown>>) : {};
    return {
        aktiv: typeof o.aktiv === 'boolean' ? o.aktiv : basis.aktiv,
        einblendung: typeof o.einblendung === 'boolean' ? o.einblendung : basis.einblendung,
        fotostempel: typeof o.fotostempel === 'boolean' ? o.fotostempel : basis.fotostempel,
    };
}

/** Gespeicherte Einstellung; fehlend/kaputt → STANDARD_GERAET (je Feld). Nichts wirft. */
export function geraetLaden(speicher: Speicher | null = standardSpeicher()): VerortungGeraet {
    if (!speicher) return { ...STANDARD_GERAET };
    try {
        const text = speicher.getItem(SCHLUESSEL);
        if (text === null) return { ...STANDARD_GERAET };
        return geraetNormalisieren(JSON.parse(text), STANDARD_GERAET);
    } catch {
        return { ...STANDARD_GERAET };
    }
}

/** Speichern; voller/fehlender Speicher ist kein Fehler (gilt dann bis zum Neuladen). */
export function geraetSpeichern(e: VerortungGeraet, speicher: Speicher | null = standardSpeicher()): void {
    if (!speicher) return;
    try {
        speicher.setItem(SCHLUESSEL, JSON.stringify(geraetNormalisieren(e, STANDARD_GERAET)));
    } catch {
        // Nur Komfort.
    }
}

// ─── React-Hook ──────────────────────────────────────────────────────────────
// Stand im Speicher der Seite (einmal aus localStorage geladen). Änderungen:
// sofort in localStorage, dann das eigene window-Ereignis an alle Hook-Nutzer
// dieser Seite; Änderungen aus anderen Tabs kommen über `storage`.

let stand: VerortungGeraet | null = null;

function geraetSchnappschuss(): VerortungGeraet {
    if (!stand) stand = geraetLaden();
    return stand;
}

function geraetAbonnieren(rueckruf: () => void): () => void {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return () => undefined;
    const eigenes = (e: Event) => {
        const detail = (e as CustomEvent<unknown>).detail;
        if (detail !== stand) stand = geraetNormalisieren(detail, geraetSchnappschuss());
        rueckruf();
    };
    const fremd = (e: StorageEvent) => {
        if (e.key !== null && e.key !== SCHLUESSEL) return;
        stand = geraetLaden();
        rueckruf();
    };
    window.addEventListener(EREIGNIS, eigenes);
    window.addEventListener('storage', fremd);
    return () => {
        window.removeEventListener(EREIGNIS, eigenes);
        window.removeEventListener('storage', fremd);
    };
}

function geraetAendern(patch: Partial<VerortungGeraet>): void {
    const naechster = geraetNormalisieren(patch, geraetSchnappschuss());
    stand = naechster;
    geraetSpeichern(naechster);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent(EREIGNIS, { detail: naechster }));
    }
}

/**
 * Geräte-Einstellung samt Setter (Teiländerung). Änderungen gehen sofort in
 * localStorage und an alle anderen Hook-Nutzer derselben Seite.
 */
export function useVerortungGeraet(): [VerortungGeraet, (patch: Partial<VerortungGeraet>) => void] {
    const geraet = useSyncExternalStore(geraetAbonnieren, geraetSchnappschuss, geraetSchnappschuss);
    const aendern = useCallback((patch: Partial<VerortungGeraet>) => geraetAendern(patch), []);
    return [geraet, aendern];
}

/**
 * Darf die Verortung (Sensoren, Kamera-Einblendung, Vorschlag) hier laufen?
 * Gerät aktiv UND Projekt aktiv UND Sensor-Brücke verfügbar (App + Plugin)
 * UND im Projekt etwas eingerichtet (Geschosse oder Gerüst) — sonst bleibt
 * alles wie bisher (Systemkamera, alte Projekte unverändert).
 */
export function verortungNutzbar(geraet: VerortungGeraet, projekt: ProjektVerortung, sensorVerfuegbar: boolean): boolean {
    return !!geraet?.aktiv && !!projekt?.aktiv && sensorVerfuegbar === true && verortungEingerichtet(projekt);
}
